import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from '../config/axios';

const AuthContext = createContext();
const isDev = process.env.NODE_ENV === 'development';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Emergency timeout: Force loading to false if stuck for too long
  useEffect(() => {
    if (loading) {
      const emergencyTimeout = setTimeout(() => {
        if (isDev) console.error('Auth loading stuck - forcing to false');
        setLoading(false);
      }, 8000);
      return () => clearTimeout(emergencyTimeout);
    }
  }, [loading]);

  const logout = useCallback(async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch {
      // Ignore - i tak czyścimy stan lokalny
    }
    // Wywal cały cache useApi — żeby kolejny user (po szybkim re-login)
    // nie zobaczył danych poprzedniego z cache.
    try {
      const { clearCache } = await import('../redesign/lib/api');
      clearCache?.();
    } catch { /* redesign chunk może nie być załadowany */ }
    setUser(null);
  }, []);

  // Sprawdź sesję przy starcie - cookie jest wysyłane automatycznie przez przeglądarkę
  useEffect(() => {
    const controller = new AbortController();

    axios.get('/api/auth/me', { signal: controller.signal })
      .then(response => {
        setUser(response.data.user);
      })
      .catch(err => {
        if (err.name === 'CanceledError') return;
        // Brak sesji lub niepoprawna - po prostu nie ustawiamy user
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });

    // Auto-logout przy 401 z dowolnego endpointu + moderacja (A1/A4).
    // Uwaga: łapiemy 403 TYLKO dla konkretnych kodów moderacji — zwykłe 403
    // (canInteract, prywatność, adminAuth) NIE mogą wylogowywać.
    const SESSION_ENDED = ['TOKEN_REVOKED', 'ACCOUNT_BANNED', 'ACCOUNT_SUSPENDED'];
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const code = error.response?.data?.code;
        if (status === 401 || (status === 403 && SESSION_ENDED.includes(code))) {
          // Komunikat dla ekranu logowania (moderacja / wygaśnięcie sesji).
          if (SESSION_ENDED.includes(code)) {
            const until = error.response?.data?.until;
            const notice = code === 'ACCOUNT_BANNED'
              ? 'Twoje konto zostało zablokowane. W razie pytań skontaktuj się z administracją.'
              : code === 'ACCOUNT_SUSPENDED'
                ? `Twoje konto jest tymczasowo zawieszone${until ? ` do ${new Date(until).toLocaleString('pl-PL')}` : ''}.`
                : 'Twoja sesja została zakończona. Zaloguj się ponownie.';
            try { sessionStorage.setItem('auth_notice', notice); } catch { /* prywatny tryb */ }
          }
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      controller.abort();
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      // Backend ustawia httpOnly cookie - my zapamiętujemy tylko user.
      // Pobierz też pełny profil (z onboarding_completed, email_verified) żeby
      // Login.jsx mogł podjąć decyzję o redirect (/onboarding vs /dashboard).
      let fullUser = response.data.user;
      try {
        const meRes = await axios.get('/api/auth/me');
        if (meRes.data?.user) fullUser = meRes.data.user;
      } catch (e) {
        // Nawet bez pełnego /me zalogowanie jest sukcesem
      }
      setUser(fullUser);
      return {
        success: true,
        user: fullUser,
        onboardingCompleted: !!fullUser?.onboarding_completed,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Błąd logowania',
        // Przepuszczamy code/email — Login.jsx pokazuje przycisk resend
        // gdy konto istnieje ale email niezweryfikowany. (UX1)
        code: error.response?.data?.code,
        email: error.response?.data?.email,
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post('/api/auth/register', {
        username,
        email,
        password
      });

      // Po rejestracji nie logujemy automatycznie - wymagana weryfikacja email
      return { success: true, requiresVerification: true, message: response.data.message };
    } catch (error) {
      // Handle validation errors array
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        return {
          success: false,
          error: errorMessages
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || 'Błąd rejestracji'
      };
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
